import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import RoutePolyline from '../../components/maps/RoutePolyline';
import api from '../../lib/api';
import { X, NavigationArrow, Clock, Path, Phone, Truck, ArrowsClockwise } from '@phosphor-icons/react';

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

// Status semantics:
//   allocated / accepted / in_progress = pilot is heading to pickup (truck unloaded).
//   pickup_reached                     = at pickup, waiting for goods.
//   pickup_done                        = goods loaded, en route to drop.
//   drop_reached                       = at drop, unloading.
const isBeforePickupStatus = (s) => ['allocated', 'accepted', 'in_progress'].includes(s);
const isAtPickupStatus = (s) => s === 'pickup_reached';
const isEnRouteStatus = (s) => ['pickup_done', 'drop_reached'].includes(s);

export default function BookingLiveTracker({ tripId, onClose }) {
  const { isLoaded } = useGoogleMaps();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastPilotPosRef = useRef('');
  // Phase 78ax (Feb-2026) — Cost-control: compute ETA ONCE on first load,
  // freeze it for the rest of the session.  Subsequent 8s polls send
  // `skip_eta=1` so the backend skips the Google Directions API call.
  // Pilot GPS still streams in real-time; only the ETA numbers are frozen.
  const etaFetchedRef = useRef(false);
  const frozenEtaRef = useRef({
    eta_to_pickup_min: null, distance_to_pickup_km: null,
    eta_to_drop_min: null, distance_to_drop_km: null,
    trip_distance_km: null, trip_duration_min: null,
    captured_at: null,
  });

  // Map control: pan to vehicle on first load and whenever the pilot moves
  // significantly. We DON'T set `center` as a controlled prop, so the user
  // is free to pan/zoom and the map won't snap back on every 8s poll.
  const mapRef = useRef(null);
  const initialCenteredRef = useRef(false);

  const [refreshingEta, setRefreshingEta] = useState(false);

  const fetchLive = useCallback(async (forceFreshEta = false) => {
    try {
      // First poll → compute ETA (Google Directions API).  Subsequent polls
      // skip Google entirely; we keep showing the value we captured first.
      // `forceFreshEta=true` (Refresh ETA button) re-runs the Google call.
      const params = (etaFetchedRef.current && !forceFreshEta) ? { skip_eta: 1 } : {};
      const { data: d } = await api.get(`/booking/trips/${tripId}/live`, { params });
      // Capture the ETA on the very first response OR when force-refreshed.
      if (!etaFetchedRef.current || forceFreshEta) {
        frozenEtaRef.current = {
          eta_to_pickup_min: d.eta_to_pickup_min,
          distance_to_pickup_km: d.distance_to_pickup_km,
          eta_to_drop_min: d.eta_to_drop_min,
          distance_to_drop_km: d.distance_to_drop_km,
          trip_distance_km: d.trip_distance_km,
          trip_duration_min: d.trip_duration_min,
          captured_at: new Date(),
        };
        etaFetchedRef.current = true;
      }
      // Always splice the frozen ETA values back into the live payload so
      // the UI keeps showing the first-computed numbers even after backend
      // returned null on subsequent polls.
      const merged = { ...d, ...frozenEtaRef.current };
      setData(merged);
    } catch {} finally { setLoading(false); }
  }, [tripId]);

  const handleRefreshEta = useCallback(async () => {
    setRefreshingEta(true);
    try { await fetchLive(true); } finally { setRefreshingEta(false); }
  }, [fetchLive]);

  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 8000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  // Smart recenter — only when:
  //   1) First time we have a pilot location, OR
  //   2) Pilot moved more than ~300m from the last known centered point.
  // This lets the booker pan around freely without the map snapping back.
  useEffect(() => {
    if (!mapRef.current || !data?.pilot_location?.lat) return;
    const { lat, lng } = data.pilot_location;
    const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    if (!initialCenteredRef.current) {
      mapRef.current.panTo({ lat, lng });
      initialCenteredRef.current = true;
      lastPilotPosRef.current = key;
      return;
    }
    // panTo only on bigger moves (~300m+) so the map doesn't constantly fight the user.
    if (lastPilotPosRef.current && lastPilotPosRef.current !== key) {
      const [oldLat, oldLng] = lastPilotPosRef.current.split(',').map(parseFloat);
      const dLat = (lat - oldLat) * 111;
      const dLng = (lng - oldLng) * 111;
      const movedKm = Math.sqrt(dLat * dLat + dLng * dLng);
      if (movedKm > 0.3) {
        mapRef.current.panTo({ lat, lng });
        lastPilotPosRef.current = key;
      }
    }
  }, [data]);

  // Other queued trips of the same pilot (for context: "Pilot Azad is also doing trips X and Y")
  const queuedTrips = useMemo(() => {
    if (!data?.pilot_trips_plan) return [];
    return data.pilot_trips_plan.filter(t => t.id !== tripId && (t.pickup_lat || t.drop_lat));
  }, [data, tripId]);

  if (loading || !data) return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="text-white text-sm">Loading live tracking...</div>
    </div>
  );

  const { trip, pilot_location, eta_to_pickup_min, distance_to_pickup_km, eta_to_drop_min, distance_to_drop_km, trip_distance_km, trip_duration_min } = data;
  const pilotPos = pilot_location?.lat ? { lat: pilot_location.lat, lng: pilot_location.lng } : null;
  const pickupPos = (trip.pickup_lat && trip.pickup_lng) ? { lat: trip.pickup_lat, lng: trip.pickup_lng } : null;
  const dropPos = (trip.drop_lat && trip.drop_lng) ? { lat: trip.drop_lat, lng: trip.drop_lng } : null;
  // Default center: pilot first, fallback to pickup, fallback to a generic SE-India center.
  const initialCenter = pilotPos || pickupPos || dropPos || { lat: 11.0, lng: 77.3 };

  const isBeforePickup = isBeforePickupStatus(trip.status);
  const isAtPickup = isAtPickupStatus(trip.status);
  const isEnRoute = isEnRouteStatus(trip.status);

  // GPS freshness — older than 5 min counts as stale.
  const gpsAgeSec = pilot_location?.updated_at ? Math.floor((Date.now() - new Date(pilot_location.updated_at).getTime()) / 1000) : null;
  const gpsFresh = gpsAgeSec !== null && gpsAgeSec < 300;
  const noCoords = !pickupPos && !dropPos;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex flex-col" data-testid="booking-live-tracker">
      {/* Top bar */}
      <div className="bg-[#0A0A0A] text-white px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <NavigationArrow size={20} className="text-[#002FA7] animate-pulse-dot" />
          <div>
            <p className="text-sm font-bold">Live Tracking — #{trip.id?.slice(0, 8)}</p>
            <p className="text-xs text-white/50">{trip.pickup_supplier_name} → {trip.drop_supplier_name}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-white/60 hover:text-white" data-testid="close-live-tracker"><X size={20} /></button>
      </div>

      <div className="flex-1 relative">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={initialCenter}
            zoom={14}
            options={{ styles: MAP_STYLES, disableDefaultUI: true, zoomControl: true, mapId: GOOGLE_MAPS_MAP_ID }}
            onLoad={(m) => { mapRef.current = m; }}
          >
            {/* RED line: Vehicle → Pickup (drawn while heading to pickup OR
                waiting at pickup).  Phase 78ax — straight geodesic line
                instead of `<RoutePolyline>` so we don't fire Google Routes
                API on every 8s poll as the pilot moves. */}
            {pilotPos && pickupPos && (isBeforePickup || isAtPickup) && (
              <Polyline
                path={[pilotPos, pickupPos]}
                options={{ strokeColor: '#FF3333', strokeWeight: 4, strokeOpacity: 0.8, geodesic: true }}
              />
            )}

            {/* GREEN route: Pickup → Drop (always shown as reference).
                Still uses Routes API but the coords never change, so this
                is one cached call per trip. */}
            {pickupPos && dropPos && (
              <RoutePolyline
                origin={pickupPos}
                destination={dropPos}
                usageKey="polyline_booking_live_tracker"
                options={{ strokeColor: '#00C853', strokeWeight: isEnRoute ? 6 : 4, strokeOpacity: isEnRoute ? 0.9 : 0.5 }}
              />
            )}

            {/* RED line: Vehicle → Drop (when en route).  Same reasoning as
                vehicle → pickup above — straight geodesic instead of
                Routes API. */}
            {pilotPos && dropPos && isEnRoute && (
              <Polyline
                path={[pilotPos, dropPos]}
                options={{ strokeColor: '#FF3333', strokeWeight: 4, strokeOpacity: 0.8, geodesic: true }}
              />
            )}

            {/* Fallback: when this trip has NO pickup/drop coords but we still
                have a live pilot location, show a small dashed circle around
                the vehicle so the booker knows it's the live position. */}
            {noCoords && pilotPos && (
              <Polyline
                path={[
                  { lat: pilotPos.lat + 0.003, lng: pilotPos.lng },
                  { lat: pilotPos.lat, lng: pilotPos.lng + 0.003 },
                  { lat: pilotPos.lat - 0.003, lng: pilotPos.lng },
                  { lat: pilotPos.lat, lng: pilotPos.lng - 0.003 },
                  { lat: pilotPos.lat + 0.003, lng: pilotPos.lng },
                ]}
                options={{ strokeColor: '#002FA7', strokeWeight: 2, strokeOpacity: 0.4, geodesic: true }}
              />
            )}

            {/* Vehicle live marker — always shown when we have a pilot GPS fix */}
            {pilotPos && (
              <AdvancedMarker
                position={pilotPos}
                contentHTML={'<div style="width:44px;height:44px;border-radius:50%;background:#002FA7;border:3px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px;font-family:system-ui,sans-serif">V</div>'}
                title={`Vehicle ${trip.vehicle_number || ''} · ${trip.pilot_name || ''} · ${Math.round(pilot_location?.speed || 0)} km/h${gpsAgeSec !== null ? ` · ${gpsAgeSec < 60 ? gpsAgeSec + 's' : Math.floor(gpsAgeSec / 60) + 'm'} ago` : ''}`}
                zIndex={10}
              />
            )}

            {/* Pickup marker */}
            {pickupPos && (
              <AdvancedMarker
                position={pickupPos}
                icon={{ color: '#00C853', label: 'P', size: 30 }}
                title={`Pickup: ${trip.pickup_supplier_name}`}
                zIndex={5}
              />
            )}

            {/* Drop marker */}
            {dropPos && (
              <AdvancedMarker
                position={dropPos}
                icon={{ color: '#FF3333', label: 'D', size: 30 }}
                title={`Drop: ${trip.drop_supplier_name}`}
                zIndex={5}
              />
            )}

            {/* Pilot's other queued trips — dimmed P/D pins so booker sees the queue */}
            {queuedTrips.map((qt, idx) => (
              <React.Fragment key={qt.id}>
                {qt.pickup_lat && qt.pickup_lng && (
                  <AdvancedMarker
                    position={{ lat: qt.pickup_lat, lng: qt.pickup_lng }}
                    icon={{ color: '#00C853', label: `P${idx + 2}`, size: 24 }}
                    opacity={0.45}
                    title={`Other trip pickup: ${qt.pickup_supplier_name || ''}`}
                    zIndex={2}
                  />
                )}
                {qt.drop_lat && qt.drop_lng && (
                  <AdvancedMarker
                    position={{ lat: qt.drop_lat, lng: qt.drop_lng }}
                    icon={{ color: '#FF3333', label: `D${idx + 2}`, size: 24 }}
                    opacity={0.45}
                    title={`Other trip drop: ${qt.drop_supplier_name || ''}`}
                    zIndex={2}
                  />
                )}
              </React.Fragment>
            ))}
          </GoogleMap>
        ) : (
          <div className="h-full flex items-center justify-center text-[#666] text-sm">Loading map...</div>
        )}

        {/* Live freshness pill (top-left) */}
        <div className="absolute top-3 left-3 z-10">
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
              gpsFresh ? 'bg-[#00C853]/90 text-white' : pilotPos ? 'bg-[#FF8C00]/90 text-white' : 'bg-[#808080]/90 text-white'
            }`}
            data-testid="live-tracker-gps-pill"
          >
            <span className={`w-1.5 h-1.5 rounded-full bg-white ${gpsFresh ? 'animate-pulse' : ''}`} />
            {gpsFresh ? 'Live' : pilotPos ? `GPS ${gpsAgeSec < 60 ? gpsAgeSec + 's' : Math.floor(gpsAgeSec / 60) + 'm'} ago` : 'No GPS yet'}
          </div>
          {queuedTrips.length > 0 && (
            <div className="mt-1 px-2 py-1 rounded-full text-[10px] font-bold bg-white/95 text-[#002FA7] uppercase tracking-widest" data-testid="live-tracker-queue-pill">
              Pilot queue: +{queuedTrips.length}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur border border-[#E5E5E5] p-2 text-[10px] space-y-1 z-10">
          <div className="flex items-center gap-2"><div className="w-5 h-1 bg-[#FF3333]" />{isEnRoute ? 'Vehicle → Drop' : 'Vehicle → Pickup'}</div>
          <div className="flex items-center gap-2"><div className="w-5 h-1 bg-[#00C853]" />Pickup → Drop</div>
        </div>

        {/* Re-center button — drops user back onto the vehicle */}
        {pilotPos && (
          <button
            onClick={() => mapRef.current?.panTo(pilotPos)}
            className="absolute bottom-[260px] right-3 z-10 bg-white border border-[#E5E5E5] rounded-full p-2 shadow hover:bg-[#F3F3F3]"
            data-testid="live-tracker-recenter-btn"
            title="Recenter on vehicle"
          >
            <NavigationArrow size={18} className="text-[#002FA7]" weight="bold" />
          </button>
        )}

        {/* Bottom overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] p-3 space-y-2">
          {/* Status */}
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${pilotPos ? 'bg-[#00C853] animate-pulse-dot' : 'bg-[#808080]'}`} />
            <span className="text-sm font-medium">
              {!pilotPos ? 'Awaiting first GPS fix from pilot…' :
                isBeforePickup ? 'Vehicle heading to pickup' :
                isAtPickup ? 'Vehicle at pickup' :
                isEnRoute ? 'En route to drop' :
                trip.status === 'completed' ? 'Delivered' : trip.status}
            </span>
          </div>

          {/* No-coords warning banner — show only the live vehicle position */}
          {noCoords && (
            <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/30 px-2 py-1.5 text-[10px] text-[#FF8C00] uppercase tracking-widest font-bold rounded">
              Pickup/Drop coordinates missing — showing only the live vehicle location.
            </div>
          )}

          {/* Phase 78ax — let the booker know the ETA is the initial estimate
              (we don't keep re-calling Google to save cost).  Live vehicle
              position keeps updating below.  Refresh button manually
              re-pulls a fresh Google ETA on demand. */}
          {frozenEtaRef.current.captured_at && (eta_to_pickup_min || eta_to_drop_min) && (
            <div className="flex items-center justify-between gap-2">
              <div
                className="text-[10px] text-[#666] uppercase tracking-widest"
                data-testid="booking-live-eta-captured-at"
              >
                ETA captured at {frozenEtaRef.current.captured_at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · vehicle position is live
              </div>
              <button
                type="button"
                onClick={handleRefreshEta}
                disabled={refreshingEta}
                className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="booking-live-refresh-eta-btn"
                title="Re-calculate ETA from Google (uses 1 API credit)"
              >
                <ArrowsClockwise size={12} className={refreshingEta ? 'animate-spin' : ''} weight="bold" />
                {refreshingEta ? 'Refreshing…' : 'Refresh ETA'}
              </button>
            </div>
          )}

          {/* ETA & Distance cards */}
          <div className="grid grid-cols-2 gap-2">
            {/* Before pickup OR at pickup: show Vehicle→Pickup ETA */}
            {(isBeforePickup || isAtPickup) && (
              <>
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-[#FF3333]" />
                    <p className="text-lg font-black text-[#FF3333]">{eta_to_pickup_min ?? '-'} min</p>
                  </div>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">ETA to Pickup</p>
                </div>
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Path size={16} className="text-[#FF3333]" />
                    <p className="text-lg font-black text-[#FF3333]">{distance_to_pickup_km ?? '-'} km</p>
                  </div>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">To Pickup</p>
                </div>
              </>
            )}

            {/* En route: show Vehicle→Drop ETA */}
            {isEnRoute && (
              <>
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-[#FF3333]" />
                    <p className="text-lg font-black text-[#FF3333]">{eta_to_drop_min ?? '-'} min</p>
                  </div>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">ETA to Drop</p>
                </div>
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Path size={16} className="text-[#FF3333]" />
                    <p className="text-lg font-black text-[#FF3333]">{distance_to_drop_km ?? '-'} km</p>
                  </div>
                  <p className="text-[9px] uppercase tracking-widest text-[#666]">To Drop</p>
                </div>
              </>
            )}

            {/* Trip info: Pickup→Drop */}
            <div className="bg-[#00C853]/5 border border-[#00C853]/20 px-3 py-2">
              <div className="flex items-center gap-2">
                <Path size={16} className="text-[#00C853]" />
                <p className="text-lg font-black text-[#00C853]">{trip_distance_km ?? '-'} km</p>
              </div>
              <p className="text-[9px] uppercase tracking-widest text-[#666]">Trip Distance</p>
            </div>
            <div className="bg-[#00C853]/5 border border-[#00C853]/20 px-3 py-2">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#00C853]" />
                <p className="text-lg font-black text-[#00C853]">{trip_duration_min ?? '-'} min</p>
              </div>
              <p className="text-[9px] uppercase tracking-widest text-[#666]">Trip Duration</p>
            </div>
          </div>

          {/* Speed */}
          {pilot_location?.speed > 0 && (
            <div className="flex items-center gap-2 text-xs text-[#666]">
              <Truck size={14} /> Speed: <strong>{Math.round(pilot_location.speed)} km/h</strong>
            </div>
          )}

          {/* Pilot info — Call button dials the pilot directly. */}
          <div className="flex items-center justify-between text-sm border-t border-[#E5E5E5] pt-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center font-bold text-base">
                {trip.pilot_name?.[0] || '?'}
              </div>
              <div>
                <p className="font-medium">{trip.pilot_name || 'Unassigned'}</p>
                <p className="text-xs text-[#666]">{trip.vehicle_number || ''}</p>
              </div>
            </div>
            {trip.pilot_mobile ? (
              <a
                href={`tel:${trip.pilot_mobile}`}
                className="flex items-center gap-1 text-[#002FA7] text-sm font-medium"
                data-testid="booking-call-pilot"
              >
                <Phone size={16} /> Call Pilot
              </a>
            ) : (
              <span className="text-xs text-[#999]">No pilot mobile</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
